import { useCallback, useState } from "react"

interface UseBulkRunDeleteParams {
  clearSelectedKeys: () => void
  closeModal: () => void
  deleteRun: (runId: string) => Promise<unknown>
  invalidateRunsTables: () => Promise<void>
  runsToDelete: string[]
  setRunsToDelete: (runIds: string[]) => void
}

interface UseBulkRunDeleteResult {
  confirmBulkDelete: () => Promise<void>
  failedRunIds: string[]
  hasPartialSuccess: boolean
  isDeleting: boolean
  resetBulkDeleteState: () => void
}

export function useBulkRunDelete({
  clearSelectedKeys,
  closeModal,
  deleteRun,
  invalidateRunsTables,
  runsToDelete,
  setRunsToDelete,
}: UseBulkRunDeleteParams): UseBulkRunDeleteResult {
  const [failedRunIds, setFailedRunIds] = useState<string[]>([])
  const [hasPartialSuccess, setHasPartialSuccess] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const resetBulkDeleteState = useCallback(() => {
    setFailedRunIds([])
    setHasPartialSuccess(false)
  }, [])

  const confirmBulkDelete = useCallback(async (): Promise<void> => {
    setIsDeleting(true)
    setFailedRunIds([])
    setHasPartialSuccess(false)

    const settledResults = await Promise.allSettled(
      runsToDelete.map((runId) => deleteRun(runId)),
    )

    setIsDeleting(false)

    const nextFailedRunIds = settledResults.flatMap((result, index) =>
      result.status === "rejected" ? [runsToDelete[index]] : [],
    )

    if (nextFailedRunIds.length > 0) {
      setFailedRunIds(nextFailedRunIds)
      setHasPartialSuccess(nextFailedRunIds.length < runsToDelete.length)
      setRunsToDelete(nextFailedRunIds)
      return
    }

    await invalidateRunsTables()
    clearSelectedKeys()
    closeModal()
  }, [
    clearSelectedKeys,
    closeModal,
    deleteRun,
    invalidateRunsTables,
    runsToDelete,
    setRunsToDelete,
  ])

  return {
    confirmBulkDelete,
    failedRunIds,
    hasPartialSuccess,
    isDeleting,
    resetBulkDeleteState,
  }
}
